// Branches — real Turkey SVG (dnomak/svg-turkiye-haritasi) loaded inline,
// Adana highlighted, three animated branch pins clustered over Adana,
// click/hover to highlight + zoom-pan with clamped bounds (mouse + touch).

import { useEffect, useRef, useState, useCallback } from 'react';
import { useContent, useLang, UI } from './content.jsx';
import { Reveal } from './primitives.jsx';

let _cachedSvg = null; // module-level cache so we fetch once

function loadTurkeySvg() {
  if (_cachedSvg) return Promise.resolve(_cachedSvg);
  return fetch('/assets/turkiye.svg')
    .then(r => r.text())
    .then(t => { _cachedSvg = t; return t; });
}

// SVG viewBox constants
const SVG_W = 1007.478;
const SVG_H = 527.323;

export function Branches() {
  const c = useContent();
  const { t } = useLang();
  const b = c.branches;
  const city = t(c.brand.city);
  const [active, setActive] = useState(b[0]?.id || 1);
  const [hovered, setHovered] = useState(null);
  const [zoom, setZoom] = useState(1.6);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // pixels, relative to container center
  const [dragging, setDragging] = useState(false);
  // Real Adana centroid from the SVG — measured at runtime via getBBox().
  const [adanaCenter, setAdanaCenter] = useState({ x: 55, y: 59 });
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const dragRef = useRef({ active: false, startX: 0, startY: 0, basePan: { x: 0, y: 0 }, moved: false });

  useEffect(() => {
    if (!b.find(x => x.id === active)) setActive(b[0]?.id);
  }, [b.length]);

  useEffect(() => {
    let mounted = true;
    loadTurkeySvg().then(svgText => {
      if (!mounted || !mapRef.current) return;
      mapRef.current.innerHTML = svgText;
      const svg = mapRef.current.querySelector('svg');
      if (!svg) return;
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.display = 'block';
      // Measure Adana's real centroid from the SVG path
      const adana = svg.querySelector('g#adana');
      if (adana) {
        try {
          const bb = adana.getBBox();
          const cx = (bb.x + bb.width / 2) / SVG_W * 100;
          const cy = (bb.y + bb.height / 2) / SVG_H * 100;
          setAdanaCenter({ x: cx, y: cy });
        } catch {}
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Auto-correct branch coords against measured Adana center.
  // Branch defaults are ~(55, 60); real Adana position comes from SVG.
  const correctX = useCallback((x) => adanaCenter.x + (x - 55), [adanaCenter.x]);
  const correctY = useCallback((y) => adanaCenter.y + (y - 60), [adanaCenter.y]);

  // Clamp pan so the scaled map always covers the container.
  const clampPan = useCallback((p, z) => {
    const el = containerRef.current;
    if (!el) return p;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const maxX = Math.max(0, (z - 1) * w / 2);
    const maxY = Math.max(0, (z - 1) * h / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    };
  }, []);

  // Move the map so the given element-% point sits at container center.
  const focusOn = useCallback((bx, by, z) => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const x = (50 - bx) / 100 * w * z;
    const y = (50 - by) / 100 * h * z;
    setPan(clampPan({ x, y }, z));
  }, [clampPan]);

  // Auto-focus when the active branch changes, or when Adana's centroid is measured.
  useEffect(() => {
    const ab = b.find(x => x.id === active);
    if (!ab) return;
    focusOn(correctX(ab.x), correctY(ab.y), zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, adanaCenter.x, adanaCenter.y]);

  // Re-clamp on window resize so pan never leaves bounds.
  useEffect(() => {
    const onResize = () => setPan(p => clampPan(p, zoom));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [zoom, clampPan]);

  const scale = zoom;
  const mapTransform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;

  const zoomIn = () => {
    const nz = Math.min(6, +(zoom + 0.5).toFixed(1));
    setZoom(nz);
    setPan(p => clampPan(p, nz));
  };
  const zoomOut = () => {
    const nz = Math.max(1, +(zoom - 0.5).toFixed(1));
    setZoom(nz);
    setPan(p => clampPan(p, nz));
  };
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const onPointerDown = (e) => {
    if (zoom <= 1) return;
    if (e.button !== undefined && e.button !== 0) return;
    if (e.target.closest && e.target.closest('button, a')) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      basePan: { ...pan },
      moved: false,
    };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    setPan(clampPan({
      x: dragRef.current.basePan.x + dx,
      y: dragRef.current.basePan.y + dy,
    }, zoom));
  };

  const onPointerUp = (e) => {
    if (!dragRef.current.active) return;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
    dragRef.current.active = false;
    setDragging(false);
  };

  return (
    <section id="stores" className="relative py-24 md:py-32 bg-cream overflow-hidden">
      <div className="absolute -left-20 top-1/3 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(245,220,212,0.5) 0%, transparent 60%)' }}/>

      {/* Province styling — applied globally to inline SVG provinces */}
      <style>{`
        .tr-map svg g { fill: #eedcbf; stroke: rgba(139,90,60,0.35); stroke-width: 0.4; transition: fill 0.35s ease, stroke 0.35s ease; cursor: default; }
        .tr-map svg g:hover { fill: #e4c79a; stroke: rgba(139,90,60,0.55); }
        .tr-map svg g#adana { fill: #8b5a3c; stroke: #4a2e1e; stroke-width: 0.8; }
        .tr-map svg g#adana:hover { fill: #6b4024; }
        .tr-map svg g#istanbul, .tr-map svg g#ankara, .tr-map svg g#izmir, .tr-map svg g#antalya, .tr-map svg g#bursa, .tr-map svg g#gaziantep, .tr-map svg g#konya { fill: #e4c79a; }
        .tr-map svg text { pointer-events: none; font-family: 'DM Mono', monospace; }
        .tr-map { transform-origin: 50% 50%; transition: transform 0.9s cubic-bezier(.22,.61,.36,1); will-change: transform; }
        .tr-map.is-dragging { transition: none; }
      `}</style>

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
          <Reveal className="md:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-tan mb-4">{t(UI.stores.kicker)} · {city}</div>
            <h2 className="font-display text-[32px] md:text-[60px] leading-[1.02] text-espresso">
              <em className="italic text-cocoa">{t(UI.stores.city)}</em> {b.length} Glamm.
            </h2>
          </Reveal>
          <Reveal delay={2} className="md:col-span-5">
            <p className="text-[14.5px] text-espresso/75 leading-relaxed">
              {b.map(x => String(t(x.name)).replace(/^Glamm\s+/i, '')).join(' · ')}
              {' — '}
              {t({ tr: 'şehrin ' + b.length + ' noktasında aynı ritüel.', en: 'the same ritual in ' + b.length + ' spots across the city.' })}
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7">
            <div ref={containerRef}
              className="relative rounded-[32px] overflow-hidden border border-espresso/10 aspect-[1007/527] select-none"
              style={{
                background: 'linear-gradient(135deg, #f6ecd9 0%, #ede0ce 100%)',
                touchAction: zoom > 1 ? 'none' : 'auto',
                cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}>

              {/* Paper grid */}
              <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                backgroundImage: 'linear-gradient(to right, rgba(74,46,30,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(74,46,30,0.05) 1px, transparent 1px)',
                backgroundSize: '44px 44px',
              }}/>

              {/* Title plate */}
              <div className="absolute top-5 left-5 z-10 bg-white/75 border border-espresso/15 rounded-xl px-4 py-2 shadow-sm">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-tan">TÜRKİYE · MAP</div>
                <div className="font-display text-[15px] italic text-espresso leading-tight">{city} · {b.length} {t(UI.stores.branchesCount)}</div>
              </div>

              {/* Zoom controls */}
              <div className="absolute top-5 right-5 z-10 flex flex-col gap-1.5">
                <button onClick={zoomIn}
                  className="bg-white/90 border border-espresso/15 rounded-full h-9 w-9 grid place-items-center hover:bg-white transition shadow-sm"
                  title={t({ tr: 'Yakınlaştır', en: 'Zoom in' })} disabled={scale >= 6}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4a2e1e" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                </button>
                <button onClick={zoomOut}
                  className="bg-white/90 border border-espresso/15 rounded-full h-9 w-9 grid place-items-center hover:bg-white transition shadow-sm"
                  title={t({ tr: 'Uzaklaştır', en: 'Zoom out' })} disabled={scale <= 1}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4a2e1e" strokeWidth="2"><path d="M5 12h14"/></svg>
                </button>
                <button onClick={resetView}
                  className="bg-white/90 border border-espresso/15 rounded-full h-9 w-9 grid place-items-center hover:bg-white transition shadow-sm text-[10px] font-mono text-espresso"
                  title={t({ tr: 'Türkiye\'yi göster', en: 'Show Türkiye' })}>
                  TR
                </button>
                <div className="bg-white/90 border border-espresso/15 rounded-full h-7 grid place-items-center px-2 font-mono text-[10px] text-espresso/70" title="Zoom level">
                  ×{scale.toFixed(1)}
                </div>
              </div>

              {/* Compass */}
              <div className="absolute bottom-5 right-5 z-10 h-12 w-12 rounded-full bg-white/80 border border-espresso/15 grid place-items-center shadow-sm">
                <svg width="28" height="28" viewBox="-15 -15 30 30">
                  <path d="M 0 -12 L 3 0 L 0 12 L -3 0 Z" fill="#4a2e1e"/>
                  <path d="M -12 0 L 0 3 L 12 0 L 0 -3 Z" fill="rgba(74,46,30,0.35)"/>
                  <text x="0" y="-14" fontSize="7" textAnchor="middle" fontFamily="DM Mono, monospace" fill="#c9a575" fontWeight="600">N</text>
                </svg>
              </div>

              {/* Turkey SVG wrapper */}
              <div className={`tr-map absolute inset-0${dragging ? ' is-dragging' : ''}`} style={{ transform: mapTransform }}>
                <div ref={mapRef} className="h-full w-full"/>

                {/* Pin overlay — auto-corrected to real Adana position */}
                {b.map(x => (
                  <BranchPin key={x.id} branch={x}
                    posX={correctX(x.x)} posY={correctY(x.y)}
                    active={active === x.id}
                    hovered={hovered === x.id}
                    onClick={() => { if (!dragRef.current.moved) setActive(x.id); }}
                    onEnter={() => setHovered(x.id)}
                    onLeave={() => setHovered(null)}
                    t={t}
                    scale={scale}/>
                ))}

                {/* Adana label when not zoomed */}
                {scale < 2 && (
                  <div className="absolute pointer-events-none" style={{ left: adanaCenter.x + '%', top: (adanaCenter.y + 3) + '%', transform: 'translate(-50%, 0)' }}>
                    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-espresso bg-cream/85 px-2 py-0.5 rounded-full shadow-sm">
                      {city}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          <Reveal delay={1} className="md:col-span-5">
            <div className="h-full flex flex-col gap-3">
              {b.map(x => (
                <BranchCard key={x.id} branch={x} active={active === x.id}
                  onClick={() => { setActive(x.id); if (scale < 2) setZoom(2.5); }}
                  t={t}/>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function BranchPin({ branch, posX, posY, active, hovered, onClick, onEnter, onLeave, t, scale }) {
  // Inverse scale to keep pin visual size constant regardless of map zoom
  const inv = 1 / scale;
  return (
    <div
      style={{ left: posX + '%', top: posY + '%', transform: `translate(-50%, -100%) scale(${inv})`, transformOrigin: 'bottom center' }}
      className="absolute z-10 cursor-pointer pointer-events-auto"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}>
      <div className={`relative transition-transform duration-300 ${active ? 'scale-110' : 'scale-95 hover:scale-105 opacity-90'}`}>
        {active && (
          <>
            <div className="absolute left-1/2 bottom-0 -translate-x-1/2 h-8 w-8 rounded-full bg-espresso/30 animate-ping"/>
            <div className="absolute left-1/2 bottom-0 -translate-x-1/2 h-12 w-12 rounded-full bg-tan/20 animate-ping" style={{ animationDelay: '.3s' }}/>
          </>
        )}
        <svg width="30" height="40" viewBox="0 0 34 44" fill="none" style={{ filter: 'drop-shadow(0 4px 6px rgba(74,46,30,0.35))' }}>
          <defs>
            <linearGradient id={`pin-g-${branch.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={active ? '#6b4024' : '#d9bf93'}/>
              <stop offset="1" stopColor={active ? '#2a1a10' : '#8b5a3c'}/>
            </linearGradient>
          </defs>
          <path d="M17 43s14-13 14-26A14 14 0 1 0 3 17c0 13 14 26 14 26z"
            fill={`url(#pin-g-${branch.id})`} stroke="#2a1a10" strokeWidth="1"/>
          <circle cx="17" cy="17" r="5.5" fill="#faf5ee"/>
          <circle cx="17" cy="17" r="2.5" fill={active ? '#4a2e1e' : '#c9a575'}/>
        </svg>
        {(hovered || active) && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 whitespace-nowrap bg-espresso text-cream text-[10px] font-mono tracking-wider px-2.5 py-1 rounded-full shadow-lg">
            {String(t(branch.name)).replace(/^Glamm\s+/i, '')}
          </div>
        )}
      </div>
    </div>
  );
}

function BranchCard({ branch, active, onClick, t }) {
  return (
    <div onClick={onClick} role="button" tabIndex={0}
      className={`text-left rounded-2xl p-5 border transition-all duration-500 cursor-pointer group relative overflow-hidden
        ${active ? 'bg-white border-espresso/20 shadow-[0_14px_40px_rgba(74,46,30,0.12)]' : 'bg-white/50 border-espresso/10 hover:bg-white/70'}`}>
      {active && (
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: 'radial-gradient(circle at 0% 0%, rgba(201,165,117,0.3) 0%, transparent 60%)',
        }}/>
      )}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-[19px] text-espresso">{t(branch.name)}</div>
            {branch.tag && <div className="font-mono text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 rounded-full bg-blush text-cocoa">{t(branch.tag)}</div>}
          </div>
          <div className="text-[12.5px] text-espresso/65 mt-1 leading-relaxed">{t(branch.addr)}</div>
        </div>
        <div className="font-mono text-[10.5px] text-espresso/55 flex-none">{branch.hours}</div>
      </div>
      {active && (
        <div className="relative mt-3 flex items-center gap-2 flex-wrap">
          {branch.mapLink && (
            <a href={branch.mapLink} target="_blank" rel="noreferrer"
              className="rounded-full bg-espresso text-cream text-[11.5px] px-3 py-1.5 hover:bg-ink transition">
              {t(UI.stores.directions)} →
            </a>
          )}
          {branch.phone && (
            <a href={`tel:${branch.phone.replace(/\s/g, '')}`}
              className="rounded-full border border-espresso/15 text-[11.5px] px-3 py-1.5 hover:bg-white/60 transition">
              {t(UI.stores.call)} · {branch.phone}
            </a>
          )}
          <div className="ml-auto flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-tan">
            <span className="h-1.5 w-1.5 rounded-full bg-green-600/80 animate-pulse"/>
            {t(UI.stores.open)}
          </div>
        </div>
      )}
    </div>
  );
}
